/**
 * Evidence collection guide dialog.
 *
 * Opens from the "How do I collect this?" link next to each evidence slot.
 * Calls POST /api/evidence-guide with (audit_id, control_id, evidence_index)
 * and renders the structured steps. A "Refresh suggestions" button bypasses
 * the server-side cache. No persisted state; checkboxes are local-only so
 * users can mentally track progress during the session.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Compass, ExternalLink, Loader2, RefreshCw, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TECH_BADGE_STYLES = {
  aws:              'bg-amber-50 text-amber-800 border-amber-200',
  google_workspace: 'bg-sky-50 text-sky-800 border-sky-200',
  github:           'bg-slate-100 text-slate-800 border-slate-300',
  okta:             'bg-indigo-50 text-indigo-800 border-indigo-200',
  azure_ad:         'bg-blue-50 text-blue-800 border-blue-200',
  manual:           'bg-slate-50 text-slate-700 border-slate-200',
};

export default function EvidenceGuideDialog({
  open,
  onClose,
  auditId,
  controlId,
  evidenceIndex,
  evidenceText,
}) {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [guide, setGuide] = useState(null);
  const [cached, setCached] = useState(false);
  const [doneSet, setDoneSet] = useState(new Set());
  const [error, setError] = useState(null);

  const authHeader = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  // Reset when the dialog opens for a different slot.
  useEffect(() => {
    if (!open) return;
    setGuide(null);
    setCached(false);
    setDoneSet(new Set());
    setError(null);
    fetchGuide(false);
    // eslint-disable-next-line
  }, [open, controlId, evidenceIndex]);

  const fetchGuide = async (forceRefresh) => {
    if (!auditId || !controlId) return;
    forceRefresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/evidence-guide`,
        { audit_id: auditId, control_id: controlId, evidence_index: evidenceIndex, force_refresh: !!forceRefresh },
        { headers: authHeader(), timeout: 35000 },
      );
      setGuide(r.data.guide);
      setCached(!!r.data.cached);
      if (forceRefresh) {
        setDoneSet(new Set());
        toast.success('Fresh guidance generated');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not generate guidance');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const toggleDone = (idx) => {
    setDoneSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const techBadgeClass = guide
    ? (TECH_BADGE_STYLES[guide.detected_tech] || TECH_BADGE_STYLES.manual)
    : '';

  const doneCount = doneSet.size;
  const total = guide?.steps?.length || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="evidence-guide-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Compass className="w-5 h-5 text-emerald-600" />
            How do I collect this?
            {guide && (
              <Badge
                variant="outline"
                className={`ml-2 ${techBadgeClass}`}
                data-testid="evidence-guide-tech-badge"
              >
                {guide.tech_label}
              </Badge>
            )}
            {cached && (
              <Badge variant="outline" className="ml-1 text-[10px] text-slate-500 border-slate-200">
                Cached
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Step-by-step instructions tailored to this evidence requirement. For SaaS-backed evidence,
            the deep-links jump straight to the right admin screen.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1">
          {/* Slot context */}
          <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm mb-4">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Evidence requirement</div>
            <div className="text-slate-800" data-testid="evidence-guide-source-requirement">
              {evidenceText || '(no description)'}
            </div>
          </div>

          {loading && (
            <div className="flex flex-col items-center justify-center py-12" data-testid="evidence-guide-loading">
              <Loader2 className="w-7 h-7 animate-spin text-emerald-600 mb-3" />
              <div className="text-sm text-slate-700">Drafting collection steps…</div>
              <div className="text-xs text-slate-500 mt-1">Usually 5–10 seconds.</div>
            </div>
          )}

          {error && !loading && (
            <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="evidence-guide-error">
              {error}
            </div>
          )}

          {guide && !loading && (
            <>
              <p className="text-sm text-slate-700 mb-4" data-testid="evidence-guide-summary">
                {guide.summary}
              </p>

              {total > 0 && (
                <div className="text-xs text-slate-500 mb-3" data-testid="evidence-guide-progress">
                  {doneCount} of {total} steps done
                </div>
              )}

              <ol className="space-y-3" data-testid="evidence-guide-steps">
                {(guide.steps || []).map((s, i) => {
                  const done = doneSet.has(i);
                  return (
                    <li
                      key={i}
                      className={`flex gap-3 rounded border p-3 transition ${
                        done ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-white'
                      }`}
                      data-testid={`evidence-guide-step-${i}`}
                    >
                      <div className="pt-0.5">
                        <Checkbox
                          checked={done}
                          onCheckedChange={() => toggleDone(i)}
                          data-testid={`evidence-guide-step-checkbox-${i}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className={`font-medium text-slate-900 ${done ? 'line-through text-slate-500' : ''}`}>
                            <span className="text-emerald-700 mr-1.5">{i + 1}.</span>
                            {s.title}
                          </div>
                          {s.action_url && (
                            <a
                              href={s.action_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 hover:underline whitespace-nowrap"
                              data-testid={`evidence-guide-step-link-${i}`}
                            >
                              Open <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <div className={`mt-1 text-sm whitespace-pre-line ${done ? 'text-slate-500' : 'text-slate-700'}`}>
                          {s.body}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>

              {Array.isArray(guide.tips) && guide.tips.length > 0 && (
                <div className="mt-5 rounded border border-amber-200 bg-amber-50/60 p-3" data-testid="evidence-guide-tips">
                  <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-1 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Tips
                  </div>
                  <ul className="list-disc pl-5 text-sm text-amber-900 space-y-1">
                    {guide.tips.map((t, i) => (<li key={i}>{t}</li>))}
                  </ul>
                </div>
              )}

              <div className="mt-4 text-xs text-slate-400">
                Model: <span className="font-mono">{guide.model}</span> ·
                Generated {guide.generated_at?.slice(0, 19).replace('T', ' ')}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="border-t border-slate-200 pt-3">
          <Button variant="ghost" onClick={() => onClose && onClose()} data-testid="evidence-guide-close">
            Close
          </Button>
          <Button
            variant="outline"
            onClick={() => fetchGuide(true)}
            disabled={loading || refreshing}
            data-testid="evidence-guide-refresh"
          >
            {refreshing
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh suggestions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
